import { ISelectionGeometryMessage } from "@kuika/kuika-cl-model";

/**
 * The latest `selectionGeometryMessage` from the canvas agent, held OUTSIDE the screen-design context.
 *
 * The agent publishes geometry on every scroll, resize and drag tick. Routing that through sd state
 * (`setScreenData`) would re-render every `withSD` consumer in the designer many times a second, so the
 * receiver writes here instead and only the selection overlay subscribes.
 *
 * `seq` orders messages within one agent lifetime. The agent restarts its counter when the canvas iframe
 * reloads (screen switch, device switch, preview), so the overlay calls `reset()` on the OLD canvas
 * window's `pagehide` (`use-canvas-frame-events.ts`) — without it the first messages of the new document
 * would all look stale and be dropped. `pagehide` fires before the new agent can send anything, where the
 * new document's `load` could arrive after its first messages.
 */
type Listener = () => void;

export class SelectionGeometryStore {
  private snapshot: ISelectionGeometryMessage | null = null;

  private lastSeq: number = -Infinity;

  private listeners: Set<Listener> = new Set<Listener>();

  getSnapshot = (): ISelectionGeometryMessage | null => this.snapshot;

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return (): void => {
      this.listeners.delete(listener);
    };
  };

  /** Stores a message from the agent; a message older than (or equal to) the last one is dropped. */
  publish = (message: ISelectionGeometryMessage): void => {
    if (message.seq <= this.lastSeq) {
      return;
    }
    this.lastSeq = message.seq;
    this.snapshot = message;
    this.emit();
  };

  /** Forgets everything, e.g. when the canvas iframe reloads and the agent's `seq` starts over. */
  reset = (): void => {
    this.lastSeq = -Infinity;
    if (this.snapshot === null) {
      return;
    }
    this.snapshot = null;
    this.emit();
  };

  private emit(): void {
    this.listeners.forEach((listener: Listener): void => listener());
  }
}

const sameList = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((value: string, index: number): boolean => value === b[index]);

/**
 * Whether two messages describe the same selection for React: everything the overlay RENDERS from —
 * which component, single/multi, the selected ids, the actions, whether it can be moved (the drag grip),
 * idle/dragging/resizing, the clip, and whether there is a rect at all. The rect's position and the
 * scroll offsets are left out: the
 * placement loop reads those live from the store every frame, so a scroll tick needs no re-render.
 */
export function isSameSelectionShape(
  a: ISelectionGeometryMessage | null,
  b: ISelectionGeometryMessage | null
): boolean {
  if (a === null || b === null) {
    return a === b;
  }
  return (
    a.kuikaId === b.kuikaId &&
    a.kind === b.kind &&
    (a.rect === null) === (b.rect === null) &&
    a.canMove === b.canMove &&
    a.interaction === b.interaction &&
    a.clipped === b.clipped &&
    sameList(a.availableActions, b.availableActions) &&
    sameList(a.selectedKuikaIds, b.selectedKuikaIds)
  );
}

export const selectionGeometryStore: SelectionGeometryStore = new SelectionGeometryStore();
